import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Text
} from '@sparrowengg/twigs-react';
import { Spinner } from 'commons/components/spinner';
import AllApps from 'features/dashboard/components/all-apps';
import { useDispatch } from 'react-redux';
import { devPortalServices } from 'commons/services';
import { setUserRedirect } from 'redux-store/actions';
import { FilterIcon, PlusIcon } from '@sparrowengg/twigs-react-icons';
import StatusIcon from 'commons/icons/status';
import Fallback from 'commons/components/fall-back';
import TicketIcon from 'commons/icons/ticket';
import { AllAppsProps } from './add-apps/types';
import { IRootDispatch } from 'commons/types';
import { appTypeConstants } from './add-apps/constants';

const Apps = ({
  apps,
  setApps,
  inputSearch,
  handleConfigPage
}: AllAppsProps) => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    devMode: false,
    published: false,
    private: false,
    public: false
  });
  const dispatch: IRootDispatch = useDispatch();

  const handleInstalledApps = async () => {
    setLoading(true);
    try {
      const data = await devPortalServices.getAllApps();
      if (data) {
        setApps(data);
        dispatch(setUserRedirect(true));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilters = (property: string, value: boolean) => {
    setFilters((prev) => ({
      ...prev,
      [property]: value
    }));
  };

  useEffect(() => {
    handleInstalledApps();
  }, []);

  const currentApps = apps.filter((app) => {
    const isMatchingStatus =
      filters.devMode && filters.published
        ? true
        : filters.devMode
          ? app?.appStatus === appTypeConstants.DRAFT ||
            app?.appType === appTypeConstants.DEVAPP
          : filters.published
            ? app?.appStatus !== appTypeConstants.DRAFT &&
              app?.appType !== appTypeConstants.DEVAPP
            : true;

    const isMatchingType =
      filters.public && filters.private
        ? true
        : filters.public
          ? app?.appType === appTypeConstants.PUBLIC
          : filters.private
            ? app?.appType !== appTypeConstants.PUBLIC
            : true;

    return (
      isMatchingStatus &&
      isMatchingType &&
      (!inputSearch ||
        app?.name?.toLowerCase().includes(inputSearch?.toLowerCase()))
    );
  });
  return (
    <>
      {loading && (
        <Flex
          alignItems="center"
          justifyContent="center"
          css={{ width: '100%', height: 'calc(100vh - 107px - 64px)' }}
        >
          <Spinner size="md" />
        </Flex>
      )}
      {apps?.length > 0 ? (
        <Flex alignItems="flex-start" gap="$16" css={{ marginTop: '$23' }}>
          <Flex flexDirection="column" gap="$16" css={{ width: 200 }}>
            <Flex alignItems="center" gap="$2">
              <FilterIcon size={20} color="#6A6A6A" />
              <Heading size="h6">Apply Filters</Heading>
            </Flex>
            <Flex flexDirection="column" gap="$4">
              <Flex alignItems="center" gap="$2">
                <StatusIcon />
                <Text size="md" weight="medium">
                  By Status
                </Text>
              </Flex>
              <Flex
                flexDirection="column"
                css={{
                  label: {
                    color: '$neutral800'
                  },
                  button: {
                    borderColor: '$neutral400'
                  }
                }}
              >
                <Box css={{ padding: '$4 $6' }}>
                  <Checkbox
                    checked={filters.devMode}
                    onChange={(value) =>
                      handleFilters('devMode', value as boolean)
                    }
                  >
                    Dev Mode
                  </Checkbox>
                </Box>
                <Box css={{ padding: '$4 $6' }}>
                  <Checkbox
                    checked={filters.published}
                    onChange={(value) =>
                      handleFilters('published', value as boolean)
                    }
                  >
                    Published
                  </Checkbox>
                </Box>
              </Flex>
            </Flex>
            <Flex flexDirection="column" gap="$4">
              <Flex alignItems="center" gap="$2">
                <StatusIcon />
                <Text size="md" weight="medium">
                  By Accessibility
                </Text>
              </Flex>
              <Flex
                flexDirection="column"
                css={{
                  label: {
                    color: '$neutral800'
                  },
                  button: {
                    borderColor: '$neutral400'
                  }
                }}
              >
                <Box css={{ padding: '$4 $6' }}>
                  <Checkbox
                    checked={filters.private}
                    onChange={(value) =>
                      handleFilters('private', value as boolean)
                    }
                  >
                    Private
                  </Checkbox>
                </Box>
                <Box css={{ padding: '$4 $6' }}>
                  <Checkbox
                    checked={filters.public}
                    onChange={(value) =>
                      handleFilters('public', value as boolean)
                    }
                  >
                    Public
                  </Checkbox>
                </Box>
              </Flex>
            </Flex>
          </Flex>
          <Flex flexDirection="column" gap="$8" css={{ width: '100%' }}>
            <AllApps apps={currentApps} />
          </Flex>
        </Flex>
      ) : (
        <Flex
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
          gap="$12"
          css={{ marginTop: 160 }}
        >
          <Fallback
            icon={<TicketIcon />}
            isBlackTheme={false}
            title="Create your first App!"
            description="Start creating your first application to witness the wonders of AppNest"
          />
          <Button
            size="lg"
            css={{ width: 'max-content' }}
            leftIcon={<PlusIcon />}
            onClick={() => handleConfigPage(true)}
          >
            New App
          </Button>
        </Flex>
      )}
    </>
  );
};

export default Apps;
